import React, { useEffect, useMemo, useState } from "react";
import { Form } from "react-router-dom";
import { toast } from "react-toastify";
import AddGameResultMutation from "./mutation/AddGameResult";
import useActiveProfile from "../common/hooks/useActiveProfile";
import useThemeColors from "../organization/portal/useThemeColors";
import { alphaFromHex } from "../../util/colors";
import useTranslation from "../../i18n/useTranslation";
import { getLocalizedGraphQLError } from "../../util/graphqlError";

const HOCKEY_STAT_KEYS = [
    "homeShotsOnGoal",
    "awayShotsOnGoal",
    "homePenaltyMinutes",
    "awayPenaltyMinutes",
    "homeSaves",
    "awaySaves",
    "homePowerPlayGoals",
    "awayPowerPlayGoals",
    "homePowerPlayOpportunities",
    "awayPowerPlayOpportunities",
    "homeHits",
    "awayHits",
    "homeBlockedShots",
    "awayBlockedShots",
];

function getHockeyStatsFromGameStats(stats = {}) {
    return HOCKEY_STAT_KEYS.reduce((result, key) => {
        result[key] = stats[key] ?? "";
        return result;
    }, {});
}

export default function GameResultModal({ game, event, updateScore, sportKey }) {
    const { t } = useTranslation();
    const colors = useThemeColors();
    const [homeScore, setHomeScore] = useState(game.scoreHome);
    const [visitorScore, setVisitorScore] = useState(game.scoreVisitor);
    const [penaltiesHome, setPenaltiesHome] = useState(game.penaltiesHome ?? "");
    const [penaltiesVisitor, setPenaltiesVisitor] = useState(game.penaltiesVisitor ?? "");
    const [resultType, setResultType] = useState(String(game.resultType || "regulation").toLowerCase());
    const gameStats = game?.gameStats || {};
    const [hockeyStats, setHockeyStats] = useState(() => getHockeyStatsFromGameStats(gameStats));
    const [eventStatus, setEventStatus] = useState(() => String(event?.status || "scheduled").toLowerCase());
    const [resetIndividualStats, setResetIndividualStats] = useState(false);
    const [addGameResultMutation] = AddGameResultMutation();

    const { activeProfile } = useActiveProfile();

    const modalId = useMemo(() => `game-result-modal${game?.id}`, [game?.id]);
    const scopeSel = useMemo(() => `#${modalId}`, [modalId]);
    const visitorName = game.visitorTeam?.name || t("gameResultModal.teams.visitor");
    const homeName = game.homeTeam?.name || t("gameResultModal.teams.home");
    const forfeitTeamOptions = useMemo(
        () =>
            [
                game.visitorTeam?.id ? { id: String(game.visitorTeam.id), name: visitorName } : null,
                game.homeTeam?.id ? { id: String(game.homeTeam.id), name: homeName } : null,
            ].filter(Boolean),
        [game.homeTeam?.id, game.visitorTeam?.id, homeName, visitorName]
    );
    const [forfeitTeamId, setForfeitTeamId] = useState(
        game.forfeitTeam?.id ? String(game.forfeitTeam.id) : ""
    );
    const isForfeit = eventStatus === "forfeit";
    const isScoreAutoAssigned = eventStatus === "forfeit" || eventStatus === "double_forfeit";
    const forfeitWinScore = Number(event?.season?.pointSystem?.forfeitWinScore ?? 3);
    const forfeitLossScore = Number(event?.season?.pointSystem?.forfeitLossScore ?? 0);
    const isSoccer = String(sportKey || "").toLowerCase() === "soccer";
    const isHockey = String(sportKey || "").toLowerCase() === "hockey";
    const isTiedScore =
        homeScore !== "" &&
        visitorScore !== "" &&
        Number(homeScore) === Number(visitorScore);
    const canEnterPenaltyShootout =
        isSoccer &&
        !isForfeit &&
        ["completed", "extra"].includes(eventStatus) &&
        isTiedScore;

    useEffect(() => {
        setEventStatus(String(event?.status || "scheduled").toLowerCase());
    }, [event?.status]);

    useEffect(() => {
        setHockeyStats(getHockeyStatsFromGameStats(gameStats));
    }, [
        game?.id,
        gameStats.homeShotsOnGoal,
        gameStats.awayShotsOnGoal,
        gameStats.homePenaltyMinutes,
        gameStats.awayPenaltyMinutes,
        gameStats.homeSaves,
        gameStats.awaySaves,
        gameStats.homePowerPlayGoals,
        gameStats.awayPowerPlayGoals,
        gameStats.homePowerPlayOpportunities,
        gameStats.awayPowerPlayOpportunities,
        gameStats.homeHits,
        gameStats.awayHits,
        gameStats.homeBlockedShots,
        gameStats.awayBlockedShots,
    ]);

    const applyForfeitScores = (status, teamId = forfeitTeamId) => {
        if (status === "double_forfeit") {
            setHomeScore(forfeitLossScore);
            setVisitorScore(forfeitLossScore);
            return;
        }

        if (status !== "forfeit" || !teamId) return;

        if (String(teamId) === String(game.homeTeam?.id)) {
            setHomeScore(forfeitLossScore);
            setVisitorScore(forfeitWinScore);
        } else if (String(teamId) === String(game.visitorTeam?.id)) {
            setHomeScore(forfeitWinScore);
            setVisitorScore(forfeitLossScore);
        }
    };

    const handleEventStatusChange = (status) => {
        setEventStatus(status);

        if (status === "scheduled") {
            setHomeScore(0);
            setVisitorScore(0);
            setPenaltiesHome("");
            setPenaltiesVisitor("");
            setResetIndividualStats(true);
            setForfeitTeamId("");
            setResultType("regulation");
        } else {
            setResetIndividualStats(false);
            if (status === "forfeit") {
                setPenaltiesHome("");
                setPenaltiesVisitor("");
                const nextForfeitTeamId = forfeitTeamId || forfeitTeamOptions[0]?.id || "";
                setForfeitTeamId(nextForfeitTeamId);
                setResultType("regulation");
                applyForfeitScores(status, nextForfeitTeamId);
            } else {
                setForfeitTeamId("");
                if (!["completed", "extra"].includes(status)) {
                    setPenaltiesHome("");
                    setPenaltiesVisitor("");
                    setResultType("regulation");
                }
                applyForfeitScores(status, "");
            }
        }
    };

    const handleForfeitTeamChange = (teamId) => {
        setForfeitTeamId(teamId);
        applyForfeitScores(eventStatus, teamId);
    };

    const handleSubmit = () => {
        if (isForfeit && !forfeitTeamId) {
            toast.error(t("gameResultModal.validation.selectForfeitTeam"), {
                autoClose: 3000,
                toastId: "game_result_forfeit_team_error",
                position: "top-center",
                closeOnClick: true,
            });
            return;
        }

        const hasPenaltyScore = penaltiesHome !== "" || penaltiesVisitor !== "";
        if (hasPenaltyScore) {
            if (!canEnterPenaltyShootout || penaltiesHome === "" || penaltiesVisitor === "") {
                toast.error(t("gameResultModal.validation.penaltyScoresRequired"), {
                    autoClose: 3000,
                    toastId: "game_result_penalty_scores_required",
                    position: "top-center",
                    closeOnClick: true,
                });
                return;
            }

            if (Number(penaltiesHome) === Number(penaltiesVisitor)) {
                toast.error(t("gameResultModal.validation.penaltyWinnerRequired"), {
                    autoClose: 3000,
                    toastId: "game_result_penalty_winner_required",
                    position: "top-center",
                    closeOnClick: true,
                });
                return;
            }
        }

        const submittedPenaltiesHome = canEnterPenaltyShootout && penaltiesHome !== ""
            ? parseInt(penaltiesHome, 10)
            : null;
        const submittedPenaltiesVisitor = canEnterPenaltyShootout && penaltiesVisitor !== ""
            ? parseInt(penaltiesVisitor, 10)
            : null;

        addGameResultMutation({
            variables: {
                profileId: parseInt(activeProfile.id, 10),
                gameId: parseInt(game.id, 10),
                scoreHome: parseInt(homeScore, 10),
                scoreVisitor: parseInt(visitorScore, 10),
                eventStatus,
                resetIndividualStats: eventStatus === "scheduled" && resetIndividualStats,
                forfeitTeamId: isForfeit ? parseInt(forfeitTeamId, 10) : null,
                penaltiesHome: submittedPenaltiesHome,
                penaltiesVisitor: submittedPenaltiesVisitor,
                resultType: isHockey ? resultType : null,
                ...(isHockey
                    ? Object.fromEntries(
                        Object.entries(hockeyStats).map(([key, value]) => [
                            key,
                            value === "" ? null : parseInt(value, 10),
                        ])
                    )
                    : {}),
            },
        })
            .then((res) => {
                if (res.data?.addGameResult) {
                    const selectedForfeitTeam = isForfeit
                        ? forfeitTeamOptions.find((team) => team.id === forfeitTeamId) || null
                        : null;
                    updateScore(
                        game.id,
                        homeScore,
                        visitorScore,
                        eventStatus.toUpperCase(),
                        selectedForfeitTeam,
                        null,
                        submittedPenaltiesHome,
                        submittedPenaltiesVisitor
                    );
                    toast.success(
                        t("gameResultModal.messages.gameResultAddedSuccessfully"),
                        {
                            autoClose: 3000,
                            toastId: "game_result_added",
                            position: "top-center",
                            closeOnClick: true,
                        }
                    );
                    setTimeout(() => window.location.reload(), 3000);
                }
            })
            .catch((err) => {
                toast.error(getLocalizedGraphQLError(err, t, "errors.somethingWentWrong"), {
                    autoClose: 3000,
                    toastId: "game_result_error",
                    position: "top-center",
                    closeOnClick: true,
                });
            });
    };

    return (
        <div className="modal fade kbola-modal" id={modalId} tabIndex="-1" role="dialog">
            <div className="modal-dialog modal-xl modal-dialog-scrollable" role="document">
                <div className="modal-content kbola-modalContent">
                    <Form
                        method="post"
                        onSubmit={(e) => {
                            e.preventDefault();
                            handleSubmit();
                        }}
                    >
                        <div className="modal-header kbola-modalHeader">
                            <div className="d-flex align-items-center" style={{ gap: 10, minWidth: 0 }}>
                                <span className="kbola-pill kbola-pillBrand">
                                    {t("gameResultModal.result")}
                                </span>
                                <h4 className="modal-title mb-0 kbola-modalTitle">
                                    {t("gameResultModal.title")}
                                </h4>
                            </div>

                            <button
                                type="button"
                                className="close kbola-closeBtn"
                                data-dismiss="modal"
                                aria-label={t("gameResultModal.close")}
                            >
                                <span aria-hidden="true">&times;</span>
                            </button>
                        </div>

                        <div className="modal-body kbola-modalBody">
                            <div className="kbola-scoreboard">
                                <div className="kbola-teamCard">
                                    <div className="kbola-teamMeta">
                                        {game.visitorTeam?.logo ? (
                                            <img
                                                className="kbola-teamLogo"
                                                src={game.visitorTeam.logo}
                                                alt={visitorName}
                                            />
                                        ) : (
                                            <span className="kbola-teamLogo kbola-teamLogoFallback">
                                                <i className="fas fa-users" aria-hidden="true"></i>
                                            </span>
                                        )}
                                        <div className="kbola-teamText">
                                            <span className="kbola-teamRole">
                                                {t("gameResultModal.teams.visitor")}
                                            </span>
                                            <span className="kbola-teamName" title={visitorName}>
                                                {visitorName}
                                            </span>
                                        </div>
                                    </div>
                                    <div className="kbola-scoreInputWrap">
                                        <label className="kbola-scoreLabel">
                                            {t("gameResultModal.headers.score")}
                                        </label>
                                        <input
                                            type="number"
                                            className="form-control kbola-control kbola-scoreInput"
                                            min="0"
                                            value={visitorScore}
                                            onChange={(e) => setVisitorScore(e.target.value)}
                                            disabled={isScoreAutoAssigned}
                                            aria-label={t("gameResultModal.aria.visitorScore")}
                                        />
                                    </div>
                                </div>

                                <div className="kbola-versus">@</div>

                                <div className="kbola-teamCard">
                                    <div className="kbola-teamMeta">
                                        {game.homeTeam?.logo ? (
                                            <img
                                                className="kbola-teamLogo"
                                                src={game.homeTeam.logo}
                                                alt={homeName}
                                            />
                                        ) : (
                                            <span className="kbola-teamLogo kbola-teamLogoFallback">
                                                <i className="fas fa-users" aria-hidden="true"></i>
                                            </span>
                                        )}
                                        <div className="kbola-teamText">
                                            <span className="kbola-teamRole">
                                                {t("gameResultModal.teams.home")}
                                            </span>
                                            <span className="kbola-teamName" title={homeName}>
                                                {homeName}
                                            </span>
                                        </div>
                                    </div>
                                    <div className="kbola-scoreInputWrap">
                                        <label className="kbola-scoreLabel">
                                            {t("gameResultModal.headers.score")}
                                        </label>
                                        <input
                                            type="number"
                                            className="form-control kbola-control kbola-scoreInput"
                                            min="0"
                                            value={homeScore}
                                            onChange={(e) => setHomeScore(e.target.value)}
                                            disabled={isScoreAutoAssigned}
                                            aria-label={t("gameResultModal.aria.homeScore")}
                                        />
                                    </div>
                                </div>
                            </div>

                            <div className="kbola-statusPanel">
                                <div className="kbola-statusRow">
                                    <label className="kbola-statusLabel" htmlFor={`${modalId}-event-status`}>
                                        <i className="fas fa-flag-checkered" aria-hidden="true"></i>
                                        {t("gameResultModal.labels.status")}
                                    </label>
                                    <select
                                        id={`${modalId}-event-status`}
                                        className="form-control kbola-control kbola-statusSelect"
                                        value={eventStatus}
                                        onChange={(e) => handleEventStatusChange(e.target.value)}
                                        aria-label={t("gameResultModal.aria.eventStatus")}
                                    >
                                        <option value="draft">
                                            {t("home.eventStatus.draft")}
                                        </option>
                                        <option value="scheduled">
                                            {t("home.eventStatus.scheduled")}
                                        </option>
                                        <option value="in_progress">
                                            {t("home.eventStatus.in_progress")}
                                        </option>
                                        <option value="completed">
                                            {t("home.eventStatus.completed")}
                                        </option>
                                        <option value="canceled">
                                            {t("home.eventStatus.canceled")}
                                        </option>
                                        <option value="forfeit">
                                            {t("home.eventStatus.forfeit")}
                                        </option>
                                        <option value="double_forfeit">
                                            {t("home.eventStatus.double_forfeit")}
                                        </option>
                                        <option value="extra">
                                            {t("home.eventStatus.extra")}
                                        </option>
                                    </select>
                                </div>

                                {isForfeit && (
                                    <div className="kbola-statusRow kbola-forfeitRow">
                                        <label className="kbola-statusLabel" htmlFor={`${modalId}-forfeit-team`}>
                                            <i className="fas fa-user-slash" aria-hidden="true"></i>
                                            {t("gameResultModal.labels.forfeitTeam")}
                                        </label>
                                        <select
                                            id={`${modalId}-forfeit-team`}
                                            className="form-control kbola-control kbola-statusSelect"
                                            value={forfeitTeamId}
                                            onChange={(e) => handleForfeitTeamChange(e.target.value)}
                                            required
                                            aria-label={t("gameResultModal.aria.forfeitTeam")}
                                        >
                                            <option value="">
                                                {t("gameResultModal.options.selectForfeitTeam")}
                                            </option>
                                            {forfeitTeamOptions.map((team) => (
                                                <option key={team.id} value={team.id}>
                                                    {team.name}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                )}

                                {canEnterPenaltyShootout && (
                                    <div className="kbola-penaltyPanel">
                                        <div className="kbola-penaltyTitle">
                                            <i className="fas fa-bullseye" aria-hidden="true"></i>
                                            {t("gameResultModal.labels.penaltyShootout")}
                                        </div>
                                        <div className="kbola-penaltyInputs">
                                            <label className="kbola-penaltyInputGroup">
                                                <span>{t("gameResultModal.teams.visitor")}</span>
                                                <input
                                                    type="number"
                                                    className="form-control kbola-control"
                                                    min="0"
                                                    value={penaltiesVisitor}
                                                    onChange={(e) => setPenaltiesVisitor(e.target.value)}
                                                    aria-label={t("gameResultModal.aria.visitorPenalties")}
                                                />
                                            </label>
                                            <span className="kbola-penaltySeparator">–</span>
                                            <label className="kbola-penaltyInputGroup">
                                                <span>{t("gameResultModal.teams.home")}</span>
                                                <input
                                                    type="number"
                                                    className="form-control kbola-control"
                                                    min="0"
                                                    value={penaltiesHome}
                                                    onChange={(e) => setPenaltiesHome(e.target.value)}
                                                    aria-label={t("gameResultModal.aria.homePenalties")}
                                                />
                                            </label>
                                        </div>
                                    </div>
                                )}

                                {isHockey && !isForfeit && (
                                    <>
                                        <div className="kbola-hockeyResultType">
                                            <div>
                                                <label className="kbola-hockeySectionTitle" htmlFor={`${modalId}-result-type`}>
                                                    {t("gameResultModal.hockey.resultType")}
                                                </label>
                                                <small>{t("gameResultModal.hockey.resultTypeHint")}</small>
                                            </div>
                                            <select
                                                id={`${modalId}-result-type`}
                                                className="form-control kbola-control kbola-hockeyResultSelect"
                                                value={resultType}
                                                onChange={(e) => setResultType(e.target.value)}
                                                disabled={!['completed', 'extra'].includes(eventStatus)}
                                            >
                                                <option value="regulation">{t("gameResultModal.hockey.regulation")}</option>
                                                <option value="overtime">{t("gameResultModal.hockey.overtime")}</option>
                                                <option value="shootout">{t("gameResultModal.hockey.shootout")}</option>
                                            </select>
                                        </div>

                                        <div className="kbola-hockeyStatsHeader">
                                            <span className="kbola-hockeySectionTitle">{t("gameResultModal.hockey.sections.teamStats")}</span>
                                            <span className="kbola-hockeyStatsRule" />
                                        </div>

                                        {[
                                            {
                                                title: t("gameResultModal.hockey.sections.scoring"),
                                                metrics: [
                                                    ["homeShotsOnGoal", "awayShotsOnGoal", "shotsOnGoal"],
                                                    ["homePowerPlayGoals", "awayPowerPlayGoals", "powerPlayGoals"],
                                                    ["homePowerPlayOpportunities", "awayPowerPlayOpportunities", "powerPlayOpportunities"],
                                                ],
                                            },
                                            {
                                                title: t("gameResultModal.hockey.sections.goalkeeping"),
                                                metrics: [["homeSaves", "awaySaves", "saves"]],
                                            },
                                            {
                                                title: t("gameResultModal.hockey.sections.discipline"),
                                                metrics: [
                                                    ["homePenaltyMinutes", "awayPenaltyMinutes", "penaltyMinutes"],
                                                    ["homeHits", "awayHits", "hits"],
                                                    ["homeBlockedShots", "awayBlockedShots", "blockedShots"],
                                                ],
                                            },
                                        ].map((group) => (
                                            <section className="kbola-hockeyStatsGroup" key={group.title}>
                                                <h5>{group.title}</h5>
                                                <div className="kbola-hockeyMetricGrid">
                                                    {group.metrics.map(([homeKey, awayKey, labelKey]) => (
                                                        <div className="kbola-hockeyMetric" key={homeKey}>
                                                            <div className="kbola-hockeyMetricLabel">
                                                                {t(`gameResultModal.hockey.stats.${labelKey}`)}
                                                            </div>
                                                            <div className="kbola-hockeyMetricInputs">
                                                                <label>
                                                                    <span>{t("gameResultModal.hockey.sections.home")}</span>
                                                                    <input
                                                                        type="number"
                                                                        min="0"
                                                                        className="form-control kbola-control"
                                                                        value={hockeyStats[homeKey]}
                                                                        onChange={(e) => setHockeyStats((current) => ({...current, [homeKey]: e.target.value}))}
                                                                    />
                                                                </label>
                                                                <label>
                                                                    <span>{t("gameResultModal.hockey.sections.visitor")}</span>
                                                                    <input
                                                                        type="number"
                                                                        min="0"
                                                                        className="form-control kbola-control"
                                                                        value={hockeyStats[awayKey]}
                                                                        onChange={(e) => setHockeyStats((current) => ({...current, [awayKey]: e.target.value}))}
                                                                    />
                                                                </label>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            </section>
                                        ))}
                                    </>
                                )}

                                {eventStatus === "scheduled" && (
                                    <>
                                        <label className="kbola-checkboxRow">
                                            <input
                                                type="checkbox"
                                                checked={resetIndividualStats}
                                                onChange={(e) => setResetIndividualStats(e.target.checked)}
                                            />
                                            <span>{t("gameResultModal.labels.resetIndividualStats")}</span>
                                        </label>

                                        <div className="alert alert-warning kbola-warningMessage" role="alert">
                                            <i className="icon fas fa-exclamation-triangle" aria-hidden="true"></i>{" "}
                                            {t(
                                                resetIndividualStats
                                                    ? "gameResultModal.scheduledStatusWarningWithIndividualStats"
                                                    : "gameResultModal.scheduledStatusWarningWithoutIndividualStats"
                                            )}
                                        </div>
                                    </>
                                )}
                            </div>

                            <div className="kbola-hint">
                                {t("gameResultModal.hint")}
                            </div>
                        </div>

                        <div className="modal-footer kbola-modalFooter justify-content-between">
                            <button
                                type="button"
                                className="btn btn-default kbola-secondaryBtn"
                                data-dismiss="modal"
                            >
                                {t("gameResultModal.close")}
                            </button>
                            <button type="submit" className="btn btn-kbola kbola-primaryBtn">
                                {t("gameResultModal.save")}
                            </button>
                        </div>
                    </Form>
                </div>
            </div>

            <style>{`
        ${scopeSel} { font-family: 'Poppins', sans-serif; }

        ${scopeSel} i.fas,
        ${scopeSel} i.far,
        ${scopeSel} i.fal,
        ${scopeSel} i.fa,
        ${scopeSel} i[class^="fa-"],
        ${scopeSel} i[class*=" fa-"] {
          font-family: "Font Awesome 5 Free" !important;
          font-weight: 900;
        }
        ${scopeSel} i.fab {
          font-family: "Font Awesome 5 Brands" !important;
          font-weight: 400;
        }

        ${scopeSel} .kbola-modalContent {
          display: flex;
          flex-direction: column;
          height: calc(100vh - 2rem);
          max-height: calc(100vh - 2rem);
          overflow: hidden !important;
          border-radius: 18px;
          overflow: hidden;
          border: 1px solid rgba(0,0,0,0.08);
          box-shadow: 0 18px 44px rgba(0,0,0,0.18);
        }

        ${scopeSel} .kbola-modalHeader {
          padding: 16px 18px;
          background: linear-gradient(
                    135deg,
                    ${alphaFromHex(colors.primary, 0.88)},
                    ${alphaFromHex(colors.primary, 0.42)}
                );
          border-bottom: 1px solid rgba(0,0,0,0.06);
        }

        ${scopeSel} .kbola-modalTitle {
          font-size: 16px;
          font-weight: 900;
          color: ${colors.textOnPrimary};
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        ${scopeSel} .kbola-closeBtn { color: ${colors.textOnPrimary}; opacity: 1; }

        ${scopeSel} .kbola-modalBody {
          flex: 1 1 auto;
          min-height: 0;
          overflow-y: auto !important;
          -webkit-overflow-scrolling: touch;
          background: linear-gradient(180deg, rgba(0,0,0,0.018), #fff 42%);
          padding: 18px;
        }

        ${scopeSel} .kbola-modalFooter {
          padding: 12px 14px;
          border-top: 1px solid rgba(0,0,0,0.06);
          background: rgba(0,0,0,0.01);
        }

        ${scopeSel} .kbola-pill {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          padding: 6px 10px;
          border-radius: 999px;
          font-weight: 900;
          font-size: 12px;
          border: 1px solid rgba(0,0,0,0.10);
          line-height: 1;
          white-space: nowrap;
        }
        ${scopeSel} .kbola-pillBrand {
          background: ${colors.primary};
          color: ${colors.textOnPrimary};
          border-color: rgba(0,0,0,0.10);
        }

        ${scopeSel} .kbola-scoreboard {
          display: grid;
          grid-template-columns: minmax(0, 1fr) auto minmax(0, 1fr);
          align-items: stretch;
          gap: 14px;
          margin-bottom: 14px;
        }

        ${scopeSel} .kbola-teamCard {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 14px;
          padding: 16px;
          border-radius: 16px;
          border: 1px solid rgba(0,0,0,0.07);
          background: #fff;
          box-shadow: 0 8px 22px rgba(0,0,0,0.06);
          min-width: 0;
        }

        ${scopeSel} .kbola-teamCard:hover {
          border-color: rgba(var(--kbs-primary-rgb, 0,173,255), 0.35);
          box-shadow: 0 10px 26px rgba(0,0,0,0.08);
        }

        ${scopeSel} .kbola-teamMeta {
          display: flex;
          align-items: center;
          gap: 12px;
          min-width: 0;
        }

        ${scopeSel} .kbola-teamLogo {
          width: 46px;
          height: 46px;
          border-radius: 14px;
          object-fit: contain;
          background: rgba(0,0,0,0.03);
          border: 1px solid rgba(0,0,0,0.08);
          flex: 0 0 auto;
        }

        ${scopeSel} .kbola-teamLogoFallback {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          color: ${colors.secondary};
        }

        ${scopeSel} .kbola-teamText {
          display: flex;
          flex-direction: column;
          min-width: 0;
        }

        ${scopeSel} .kbola-teamRole,
        ${scopeSel} .kbola-scoreLabel {
          font-weight: 900;
          font-size: 11px;
          letter-spacing: 0.04em;
          color: rgba(20,21,26,0.56);
          text-transform: uppercase;
        }

        ${scopeSel} .kbola-scoreLabel {
          margin: 0 0 4px;
          text-align: center;
        }

        ${scopeSel} .kbola-versus {
          display: flex;
          align-items: center;
          justify-content: center;
          align-self: center;
          width: 38px;
          height: 38px;
          border-radius: 999px;
          background: ${colors.primary};
          color: ${colors.textOnPrimary};
          font-weight: 900;
          box-shadow: 0 8px 18px ${alphaFromHex(colors.primary, 0.28)};
        }

        ${scopeSel} .kbola-statusPanel {
          padding: 14px;
          border-radius: 16px;
          background: rgba(0,0,0,0.025);
          border: 1px solid rgba(0,0,0,0.06);
          margin-bottom: 12px;
        }

        ${scopeSel} .kbola-hockeyResultType {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 16px;
          padding: 12px 14px;
          margin: 14px 0;
          border: 1px solid rgba(0,0,0,0.07);
          border-left: 4px solid ${colors.primary};
          border-radius: 12px;
          background: #fff;
        }

        ${scopeSel} .kbola-hockeyResultType small {
          display: block;
          max-width: 560px;
          margin-top: 3px;
          color: rgba(20,21,26,0.52);
          font-size: 11px;
        }

        ${scopeSel} .kbola-hockeySectionTitle {
          display: block;
          color: rgba(20,21,26,0.72);
          font-size: 11px;
          font-weight: 900;
          letter-spacing: 0.05em;
          text-transform: uppercase;
        }

        ${scopeSel} .kbola-hockeyResultSelect {
          width: 170px;
          min-height: 40px;
          flex: 0 0 auto;
        }

        ${scopeSel} .kbola-hockeyStatsHeader {
          display: flex;
          align-items: center;
          gap: 10px;
          margin: 18px 0 10px;
        }

        ${scopeSel} .kbola-hockeyStatsRule {
          height: 1px;
          flex: 1;
          background: rgba(0,0,0,0.09);
        }

        ${scopeSel} .kbola-hockeyStatsGroup {
          padding: 13px 14px 14px;
          margin-bottom: 10px;
          border: 1px solid rgba(0,0,0,0.07);
          border-radius: 14px;
          background: rgba(255,255,255,0.78);
        }

        ${scopeSel} .kbola-hockeyStatsGroup h5 {
          margin: 0 0 11px;
          color: ${colors.secondary};
          font-size: 13px;
          font-weight: 900;
        }

        ${scopeSel} .kbola-hockeyMetricGrid {
          display: grid;
          grid-template-columns: repeat(3, minmax(0, 1fr));
          gap: 10px;
        }

        ${scopeSel} .kbola-hockeyMetric {
          min-width: 0;
          padding: 10px;
          border-radius: 11px;
          background: rgba(0,0,0,0.025);
        }

        ${scopeSel} .kbola-hockeyMetricLabel {
          min-height: 28px;
          margin-bottom: 7px;
          color: rgba(20,21,26,0.7);
          font-size: 11px;
          font-weight: 850;
        }

        ${scopeSel} .kbola-hockeyMetricInputs {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 7px;
        }

        ${scopeSel} .kbola-hockeyMetricInputs label {
          margin: 0;
          color: rgba(20,21,26,0.5);
          font-size: 9px;
          font-weight: 800;
          text-transform: uppercase;
        }

        ${scopeSel} .kbola-hockeyMetricInputs input {
          min-height: 36px;
          margin-top: 4px;
          padding: 5px 7px;
          border-radius: 9px;
          font-size: 13px;
        }

        ${scopeSel} .kbola-statusRow {
          display: grid;
          grid-template-columns: 1fr 220px;
          gap: 12px;
          align-items: center;
          margin-bottom: ${eventStatus === "scheduled" || isForfeit ? "10px" : "0"};
        }

        ${scopeSel} .kbola-statusLabel {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          margin: 0;
          font-weight: 900;
          font-size: 14px;
          color: ${colors.woodsmoke};
        }

        ${scopeSel} .kbola-statusSelect {
          padding: 10px 12px;
        }

        ${scopeSel} .kbola-penaltyPanel {
          margin-top: 12px;
          padding: 12px;
          border-radius: 12px;
          background: ${alphaFromHex(colors.primary, 0.06)};
          border: 1px solid ${alphaFromHex(colors.primary, 0.18)};
        }

        ${scopeSel} .kbola-penaltyTitle {
          display: flex;
          align-items: center;
          gap: 8px;
          margin-bottom: 10px;
          color: ${colors.woodsmoke};
          font-size: 13px;
          font-weight: 900;
        }

        ${scopeSel} .kbola-penaltyInputs {
          display: flex;
          align-items: flex-end;
          justify-content: center;
          gap: 10px;
        }

        ${scopeSel} .kbola-penaltyInputGroup {
          display: flex;
          flex-direction: column;
          gap: 4px;
          margin: 0;
          color: rgba(20,21,26,0.65);
          font-size: 11px;
          font-weight: 900;
          text-transform: uppercase;
        }

        ${scopeSel} .kbola-penaltyInputGroup input {
          width: 100px;
          text-align: center;
        }

        ${scopeSel} .kbola-penaltySeparator {
          padding-bottom: 10px;
          color: ${colors.woodsmoke};
          font-size: 18px;
          font-weight: 900;
        }

        ${scopeSel} .kbola-checkboxRow {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 11px 12px;
          border-radius: 12px;
          border: 1px solid rgba(255, 193, 7, 0.36);
          background: rgba(255, 193, 7, 0.08);
          color: ${colors.woodsmoke};
          font-size: 13px;
          font-weight: 900;
          margin-bottom: 10px;
        }

        ${scopeSel} .kbola-checkboxRow input {
          width: 16px;
          height: 16px;
        }

        ${scopeSel} .kbola-warningMessage {
          padding: 10px 12px;
          border-radius: 12px;
          font-size: 12px;
          font-weight: 800;
          margin-bottom: 10px;
        }

        ${scopeSel} .kbola-teamName {
          font-weight: 900;
          font-size: 15px;
          color: ${colors.woodsmoke};
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        ${scopeSel} .kbola-control {
          border-radius: 12px;
          border: 1px solid rgba(0,0,0,0.10);
          font-weight: 900;
          font-size: 14px;
          box-shadow: none;
          height: 44px;
        }

        ${scopeSel} .kbola-scoreInput {
          text-align: center;
          padding: 10px;
          width: 96px;
          height: 54px;
          font-size: 22px;
        }

        ${scopeSel} .kbola-control:focus {
          outline: none;
          box-shadow: 0 0 0 0.2rem rgba(var(--kbs-primary-rgb, 0,173,255), 0.18);
          border-color: rgba(var(--kbs-primary-rgb, 0,173,255), 0.75);
        }

        ${scopeSel} .kbola-hint {
          margin-top: 10px;
          padding: 10px 12px;
          border-radius: 12px;
          background: ${alphaFromHex(colors.secondary, 0.08)};
          font-size: 12px;
          font-weight: 800;
          color: ${colors.secondary};
        }

        ${scopeSel} .kbola-primaryBtn {
          border-radius: 12px;
          font-weight: 900;
          padding: 10px 14px;
          background: ${colors.primary};
          color: ${colors.textOnPrimary};
        }
        
        ${scopeSel} .kbola-secondaryBtn {
          border-radius: 12px;
          font-weight: 900;
          padding: 10px 14px;
        }

        ${scopeSel}.modal {
          overflow-y: auto;
        }

        ${scopeSel} .modal-dialog {
          width: calc(100vw - 2rem);
          max-width: 1600px;
          max-height: calc(100% - 1.75rem * 2);
        }

        ${scopeSel} .kbola-modalBody {
          max-height: calc(100vh - 210px);
          overflow-y: auto !important;
          -webkit-overflow-scrolling: touch;
        }

        @media (max-width: 576px) {
          ${scopeSel} .kbola-scoreboard {
            grid-template-columns: 1fr;
          }
          ${scopeSel} .kbola-versus {
            justify-self: center;
          }
          ${scopeSel} .kbola-statusRow {
            grid-template-columns: 1fr;
          }
          ${scopeSel} .kbola-teamCard {
            align-items: stretch;
          }
          ${scopeSel} .kbola-scoreInputWrap {
            flex: 0 0 auto;
          }
          ${scopeSel} .kbola-hockeyResultType {
            align-items: stretch;
            flex-direction: column;
          }
          ${scopeSel} .kbola-hockeyResultSelect {
            width: 100%;
          }
          ${scopeSel} .kbola-hockeyMetricGrid {
            grid-template-columns: 1fr;
          }
          ${scopeSel} .modal-dialog {
            width: calc(100vw - 1rem);
            margin: 0.5rem auto;
          }
        }
      `}</style>
        </div>
    );
}
